import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions } from '@/api';
import { updateResponsiveDialogConfig } from '@/stores/DrawerStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

import { ReviewDetailContent } from '../ReviewDetailContent';
import { PhotoReviewItem } from './PhotoReviewItem';
import { SideNavButtons } from './SideNavButtons';

interface PhotoReviewsModalContentProps {
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  productNumber?: string;
  institutionId?: number;
  initialReviewId?: number;
}

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 10;

export const PhotoReviewsModalContent = ({
  productNumber,
  institutionId,
  initialReviewId,
}: PhotoReviewsModalContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonPositions, setButtonPositions] = useState({
    left: { top: 0, left: 0 },
    right: { top: 0, right: 0 },
  });

  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(
    initialReviewId ?? null,
  );

  //* 더보기 포토후기 목록
  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(
      UnauthInfiniteQueryOptions['get:/wello/v2/allows/home/gift-review/photo'](
        {
          query: {
            productNumber,
            institutionId,
            page: INITIAL_PAGE,
            size: INITIAL_SIZE,
          },
        },
      ),
    ),
    enabled: !!productNumber || !!institutionId,
  });

  const { ref, inView } = useInView();

  const infinitePhotoReviewList =
    data?.pages.flatMap((page) => page.context?.contents ?? []) ?? [];

  //* 버튼 위치 업데이트 함수
  const updateButtonPositions = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;

    setButtonPositions({
      left: {
        top: centerY,
        left: rect.left - 120, // -left-20 (120px) 위치
      },
      right: {
        top: centerY,
        right: window.innerWidth - rect.right - 120, // -right-20 (120px) 위치
      },
    });
  };

  //* containerRef.current가 설정될 때와 크기 변화 감지
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 초기 위치 업데이트
    updateButtonPositions();

    // ResizeObserver로 컨테이너 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      updateButtonPositions();
    });

    resizeObserver.observe(container);

    // 윈도우 이벤트 리스너
    const handleResize = () => updateButtonPositions();
    const handleScroll = () => updateButtonPositions();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [infinitePhotoReviewList.length, selectedReviewId]); // selectedReviewId 추가하여 컨텐츠 변경 시에도 업데이트

  //* 무한 스크롤
  useEffect(() => {
    if (!isFetching && inView) fetchNextPage();
  }, [inView, isFetching, fetchNextPage, hasNextPage]);

  const handleDetailReviewId = (reviewId: number) => {
    setSelectedReviewId(reviewId);

    updateResponsiveDialogConfig({
      modalHeader: '포토후기',
    });
  };

  const selectedReviewIndex = infinitePhotoReviewList.findIndex(
    (item) => item.reviewId === selectedReviewId,
  );
  const prevReviewIndex = selectedReviewIndex - 1;
  const nextReviewIndex = selectedReviewIndex + 1;
  const isPrevDisabled = prevReviewIndex < 0;
  const isNextDisabled = nextReviewIndex >= infinitePhotoReviewList.length;
  const isLastReview =
    selectedReviewIndex === infinitePhotoReviewList.length - 1;

  const handlePrevClick = () => {
    if (!isPrevDisabled) {
      setSelectedReviewId(infinitePhotoReviewList[prevReviewIndex].reviewId);
    }
  };

  const handleNextClick = () => {
    if (!isNextDisabled) {
      setSelectedReviewId(infinitePhotoReviewList[nextReviewIndex].reviewId);
    }
  };

  //* 마지막 포토후기일 때 다음 페이지 로딩
  useEffect(() => {
    if (isLastReview) {
      fetchNextPage();
    }
  }, [isLastReview, fetchNextPage]);

  return (
    <>
      <div ref={containerRef} className="grow">
        {selectedReviewId ? (
          <div className="min-h-[544px]">
            <ReviewDetailContent reviewId={selectedReviewId} />
          </div>
        ) : (
          <>
            <div
              className={cn(
                'relative grid min-h-[544px] grid-cols-7 gap-8',
                'mobile-tablet:h-full mobile-tablet:grid-cols-3',
              )}
            >
              {infinitePhotoReviewList.map(
                ({ fileUrl: file_url, reviewId: review_id, productName: product_name }) => (
                  <PhotoReviewItem
                    key={review_id}
                    alt={product_name}
                    src={file_url}
                    onClick={() => handleDetailReviewId(review_id)}
                  />
                ),
              )}
            </div>
            {hasNextPage ? (
              <div className="my-30 w-full flex-center" ref={ref}>
                <Spinner />
              </div>
            ) : null}
          </>
        )}
      </div>
      {selectedReviewId ? (
        <SideNavButtons
          positions={buttonPositions}
          onNextClick={handleNextClick}
          onPrevClick={handlePrevClick}
          isPrevDisabled={isPrevDisabled}
          isNextDisabled={isNextDisabled}
        />
      ) : null}
    </>
  );
};
